import React, { useEffect, useRef, useState } from 'react';
import * as api from '../../api';
import { Card, Input, Select, DatePicker, Textarea, useToast } from '../UI';
import { useUOM } from '../../hooks/useUOM';

// ─── Success modal ────────────────────────────────────────────────────────────

function SuccessModal({ submitted, requestRef, onOk }) {
  const title    = submitted ? 'Submitted for CC Review' : 'Saved as Draft';
  const subtitle = submitted
    ? 'Your material request has been submitted and is now awaiting Cost Control review.'
    : 'Your material request has been saved as a draft. You can edit and submit it later.';
  const ref = requestRef?.request_number || requestRef?.request_ref || requestRef?.id || null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 18,
        padding: '44px 48px', maxWidth: 440, width: '90%',
        boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0,
        textAlign: 'center',
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: submitted ? '#dcfce7' : '#dbeafe',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 20,
        }}>
          {submitted ? (
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={submitted ? '#16a34a' : '#1d4ed8'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          ) : (
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
          )}
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 13.5, color: '#475569', lineHeight: 1.6, marginBottom: ref ? 16 : 28 }}>{subtitle}</div>
        {ref && (
          <div style={{
            background: '#f1f5f9', border: '1px solid #e2e8f0',
            borderRadius: 8, padding: '8px 18px',
            fontSize: 13, fontWeight: 700, color: '#1e3a8a',
            marginBottom: 28, letterSpacing: '0.03em',
          }}>Ref: {ref}</div>
        )}
        <button
          onClick={onOk}
          style={{
            padding: '11px 48px', borderRadius: 10, border: 'none',
            background: submitted ? '#16a34a' : '#1e3a8a',
            color: '#fff', fontWeight: 700, fontSize: 14,
            cursor: 'pointer', width: '100%',
            boxShadow: submitted ? '0 4px 14px rgba(22,163,74,.3)' : '0 4px 14px rgba(30,58,138,.3)',
          }}
        >OK</button>
      </div>
    </div>
  );
}

// ─── Priority options ─────────────────────────────────────────────────────────

const PRIORITY_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'high',   label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

// ─── Empty state factories ────────────────────────────────────────────────────

let _lineId = 0;
let _subId  = 0;

const newSubItem = () => ({
  _sid: ++_subId,
  material_id:          '',
  material_description: '',
  specification:        '',
  brand_model:          '',
  required_quantity:    '',
  unit_of_measure:      '',
  estimated_unit_rate:  '',
  estimated_total:      '',
});

const newLine = () => ({
  _id:                 ++_lineId,
  boq_id:              '',
  boq_item_id:         '',
  required_quantity:   '',
  unit_of_measure:     '',
  estimated_unit_rate: '',
  estimated_total:     '',
  remarks:             '',
  sub_items:           [newSubItem()],
});

let _catId = 0;
const newCategory = () => ({
  _id: ++_catId,
  category_name: '',
  line_items: [newLine()],
});

// ─── Shared cell-input style ──────────────────────────────────────────────────

const cellInp = {
  background: 'var(--bg-base)', border: '1px solid var(--border-light)',
  borderRadius: 11, padding: '10px 13px', color: 'var(--text-primary)',
  fontSize: 13.5, width: '100%', boxSizing: 'border-box', outline: 'none',
  fontFamily: 'var(--font)',
};

const subCellInp = {
  ...cellInp,
  fontSize: 11.5, padding: '6px 9px', borderRadius: 8,
};

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);

// ─── Material sub-item row ────────────────────────────────────────────────────

function SubItemRow({ sub, onChange, onRemove, materials, materialOptions, uomOptions }) {
  const set = (k) => (e) => onChange({ ...sub, [k]: e.target.value });

  const handleMaterialChange = (materialId) => {
    if (!materialId) {
      onChange({ ...sub, material_id: '', material_description: '', unit_of_measure: '', estimated_unit_rate: '', estimated_total: '' });
      return;
    }
    if (materialId === 'others') {
      onChange({ ...sub, material_id: 'others', material_description: '' });
      return;
    }
    const mat = materials.find(m => String(m.material_id) === String(materialId));
    const rate  = mat?.standard_rate ? String(mat.standard_rate) : sub.estimated_unit_rate;
    const qty   = sub.required_quantity;
    const total = qty && rate ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : sub.estimated_total;
    onChange({
      ...sub,
      material_id:          materialId,
      material_description: mat ? (mat.name || mat.material_name || '') : '',
      unit_of_measure:      mat?.unit_of_measure || sub.unit_of_measure,
      estimated_unit_rate:  rate,
      estimated_total:      total,
    });
  };

  const handleQty = (e) => {
    const qty   = e.target.value;
    const rate  = sub.estimated_unit_rate;
    const total = qty && rate ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : sub.estimated_total;
    onChange({ ...sub, required_quantity: qty, estimated_total: total });
  };

  const handleRate = (e) => {
    const rate  = e.target.value;
    const qty   = sub.required_quantity;
    const total = qty && rate ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : sub.estimated_total;
    onChange({ ...sub, estimated_unit_rate: rate, estimated_total: total });
  };

  return (
    <tr style={{ background: '#fefcfd', borderBottom: '1px solid #fce7f3' }}>

      {/* Material * */}
      <td style={{ padding: '3px 5px', minWidth: 150 }}>
        <Select
          title="Select Material"
          value={sub.material_id}
          onChange={handleMaterialChange}
          options={materialOptions}
          style={!sub.material_id ? { border: '1.5px solid #fca5a5', fontSize: 11 } : { fontSize: 11 }}
        />
        {sub.material_id === 'others' && (
          <input
            style={{ ...subCellInp, marginTop: 3 }}
            value={sub.material_description}
            onChange={set('material_description')}
            placeholder="Specify material name…"
            autoFocus
          />
        )}
      </td>

      {/* Description */}
      <td style={{ padding: '3px 5px' }}>
        <input style={{ ...subCellInp, minWidth: 120 }} value={sub.material_description} onChange={set('material_description')} placeholder="Description" />
      </td>

      {/* Specification */}
      <td style={{ padding: '3px 5px' }}>
        <input style={{ ...subCellInp, minWidth: 90 }} value={sub.specification} onChange={set('specification')} placeholder="Spec" />
      </td>

      {/* Brand */}
      <td style={{ padding: '3px 5px' }}>
        <input style={{ ...subCellInp, minWidth: 80 }} value={sub.brand_model} onChange={set('brand_model')} placeholder="Brand" />
      </td>

      {/* Qty */}
      <td style={{ padding: '3px 5px' }}>
        <input style={{ ...subCellInp, width: 72, textAlign: 'center' }} type="number" value={sub.required_quantity} onChange={handleQty} placeholder="Qty" />
      </td>

      {/* UOM */}
      <td style={{ padding: '3px 5px', minWidth: 85 }}>
        <Select title="UOM" value={sub.unit_of_measure} onChange={(v) => onChange({ ...sub, unit_of_measure: v })} options={uomOptions} style={{ fontSize: 11 }} />
      </td>

      {/* Unit Rate */}
      <td style={{ padding: '3px 5px' }}>
        <input style={{ ...subCellInp, width: 85, textAlign: 'center' }} type="number" value={sub.estimated_unit_rate} onChange={handleRate} placeholder="Rate" />
      </td>

      {/* Est. Cost (read-only) */}
      <td style={{ padding: '3px 5px' }}>
        <div style={{
          ...subCellInp, width: 95, textAlign: 'center',
          background: '#fef2f7', color: '#be185d', cursor: 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {sub.estimated_total !== '' ? Number(sub.estimated_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
        </div>
      </td>

      {/* Remove */}
      <td style={{ padding: '3px 5px', textAlign: 'center' }}>
        <button
          onClick={onRemove}
          title="Remove material line"
          style={{ padding: '4px 6px', borderRadius: 5, border: 'none', cursor: 'pointer', background: '#fee2e2', color: '#dc2626', display: 'inline-flex', alignItems: 'center' }}
        ><TrashIcon /></button>
      </td>
    </tr>
  );
}

// ─── Main BOQ line row ────────────────────────────────────────────────────────

const SUB_HEADERS = ['Material *', 'Description', 'Specification', 'Brand', 'Qty', 'UOM', 'Unit Rate', 'Est. Cost', ''];
const MAIN_COL_COUNT = 8; // BOQ | Sub BOQ | Qty | UOM | Unit Rate | Est. Cost | Remarks | Actions

function LineRow({ line, onChange, onRemove, showRemove, materials, materialOptions, boqDocs, boqItemsCache, loadBoqItems, uomOptions }) {
  const handleBoqChange = (boqDocId) => {
    onChange({ ...line, boq_id: boqDocId, boq_item_id: '' });
    if (boqDocId) loadBoqItems(boqDocId);
  };

  const subBoqItems = line.boq_id ? (boqItemsCache[line.boq_id] || []) : [];

  const handleSubBoqChange = (boqItemId) => {
    if (!boqItemId) { onChange({ ...line, boq_item_id: '' }); return; }
    const item  = subBoqItems.find(i => String(i.boq_item_id || i.id) === String(boqItemId));
    const rate  = item?.unit_rate ? String(item.unit_rate) : line.estimated_unit_rate;
    const qty   = line.required_quantity;
    const total = qty && rate ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : line.estimated_total;
    onChange({
      ...line,
      boq_item_id:         boqItemId,
      unit_of_measure:     item?.unit_of_measure || line.unit_of_measure,
      estimated_unit_rate: rate,
      estimated_total:     total,
    });
  };

  const handleQty = (e) => {
    const qty   = e.target.value;
    const rate  = line.estimated_unit_rate;
    const total = qty && rate ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : line.estimated_total;
    onChange({ ...line, required_quantity: qty, estimated_total: total });
  };

  const handleRate = (e) => {
    const rate  = e.target.value;
    const qty   = line.required_quantity;
    const total = qty && rate ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : line.estimated_total;
    onChange({ ...line, estimated_unit_rate: rate, estimated_total: total });
  };

  const addSubItem    = () => onChange({ ...line, sub_items: [...line.sub_items, newSubItem()] });
  const removeSubItem = (sid) => onChange({ ...line, sub_items: line.sub_items.filter(s => s._sid !== sid) });
  const updateSubItem = (sid, updated) => onChange({
    ...line,
    sub_items: line.sub_items.map(s => s._sid === sid ? updated : s),
  });

  const boqOptions = [
    { value: '', label: '— None —', alwaysVisible: true },
    ...boqDocs.map(b => ({ value: b.id, label: b.name })),
  ];
  const subBoqOptions = [
    { value: '', label: '— None —', alwaysVisible: true },
    ...subBoqItems.map(item => ({ value: item.boq_item_id || item.id, label: `${item.boq_code} — ${item.description}` })),
  ];

  const hasSubs = line.sub_items.length > 0;

  return (
    <>
      {/* ── Main BOQ line ── */}
      <tr style={{ borderBottom: hasSubs ? 'none' : '1px solid var(--border)', background: 'var(--bg-base)' }}>

        {/* BOQ */}
        <td style={{ padding: '5px 6px', minWidth: 155 }}>
          <Select
            title="Select BOQ"
            value={line.boq_id}
            onChange={handleBoqChange}
            options={boqOptions}
            disabled={boqDocs.length === 0}
            placeholder="— None —"
          />
        </td>

        {/* Sub BOQ */}
        <td style={{ padding: '5px 6px', minWidth: 165 }}>
          <Select
            title="Select Sub BOQ Item"
            value={line.boq_item_id}
            onChange={handleSubBoqChange}
            options={subBoqOptions}
            disabled={!line.boq_id || subBoqItems.length === 0}
            placeholder="— None —"
          />
        </td>

        {/* Qty */}
        <td style={{ padding: '5px 6px', textAlign: 'center' }}>
          <input style={{ ...cellInp, width: 90, textAlign: 'center' }} type="number" value={line.required_quantity} onChange={handleQty} placeholder="Qty" />
        </td>

        {/* UOM */}
        <td style={{ padding: '5px 6px', textAlign: 'center', minWidth: 90 }}>
          <Select
            title="Unit of Measure"
            value={line.unit_of_measure}
            onChange={(v) => onChange({ ...line, unit_of_measure: v })}
            options={uomOptions}
          />
        </td>

        {/* Unit Rate */}
        <td style={{ padding: '5px 6px', textAlign: 'center' }}>
          <input style={{ ...cellInp, width: 100, textAlign: 'center' }} type="number" value={line.estimated_unit_rate} onChange={handleRate} placeholder="Rate" />
        </td>

        {/* Est. Cost (read-only) */}
        <td style={{ padding: '5px 6px', textAlign: 'center' }}>
          <div style={{
            ...cellInp, width: 110, textAlign: 'center',
            background: '#f1f5f9', color: '#374151', cursor: 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {line.estimated_total !== '' ? Number(line.estimated_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
          </div>
        </td>

        {/* Remarks */}
        <td style={{ padding: '5px 6px' }}>
          <input
            style={{ ...cellInp, minWidth: 140 }}
            value={line.remarks}
            onChange={(e) => onChange({ ...line, remarks: e.target.value })}
            placeholder="Remarks"
          />
        </td>

        {/* Actions */}
        <td style={{ padding: '5px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>
          <button
            onClick={addSubItem}
            title="Add material line"
            style={{
              padding: '5px 10px', borderRadius: 6, border: 'none',
              cursor: 'pointer', background: '#fce7f3', color: '#9d174d',
              fontWeight: 700, fontSize: 11, marginRight: showRemove ? 4 : 0,
            }}
          >+ Material</button>
          {showRemove && (
            <button
              onClick={onRemove}
              title="Remove line"
              style={{ padding: '5px 7px', borderRadius: 6, border: 'none', cursor: 'pointer', background: '#fee2e2', color: '#dc2626', display: 'inline-flex', alignItems: 'center' }}
            ><TrashIcon /></button>
          )}
        </td>
      </tr>

      {/* ── Material sub-rows ── */}
      {hasSubs && (
        <tr style={{ borderBottom: '1px solid #fecdd3' }}>
          <td colSpan={MAIN_COL_COUNT} style={{ padding: '0 8px 10px 32px', background: '#fffbfc' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
              <thead>
                <tr style={{ background: '#fff0f4' }}>
                  {SUB_HEADERS.map(h => (
                    <th key={h} style={{
                      padding: '5px 6px', textAlign: 'left',
                      fontWeight: 700, fontSize: 9.5, textTransform: 'uppercase',
                      letterSpacing: '0.06em', color: '#be185d', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {line.sub_items.map(sub => (
                  <SubItemRow
                    key={sub._sid}
                    sub={sub}
                    onChange={(updated) => updateSubItem(sub._sid, updated)}
                    onRemove={() => removeSubItem(sub._sid)}
                    materials={materials}
                    materialOptions={materialOptions}
                    uomOptions={uomOptions}
                  />
                ))}
              </tbody>
            </table>
            <button
              onClick={addSubItem}
              style={{
                marginTop: 7, padding: '3px 14px',
                borderRadius: 5, border: '1px dashed #f9a8d4',
                background: '#fdf2f8', color: '#9d174d',
                cursor: 'pointer', fontWeight: 700, fontSize: 11,
              }}
            >+ Add Material Line</button>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Category block ───────────────────────────────────────────────────────────

const MAIN_HEADERS = ['BOQ', 'Sub BOQ', 'Qty', 'UOM', 'Unit Rate', 'Est. Cost', 'Remarks', ''];

function CategoryBlock({ cat, onUpdate, onRemove, showRemove, index, materials, materialOptions, boqDocs, boqItemsCache, loadBoqItems, uomOptions }) {
  const setName = (e) => onUpdate({ ...cat, category_name: e.target.value });

  const updateLine = (lineId, updatedLine) => onUpdate({
    ...cat,
    line_items: cat.line_items.map(l => l._id === lineId ? updatedLine : l),
  });
  const removeLine = (lineId) => onUpdate({
    ...cat,
    line_items: cat.line_items.filter(l => l._id !== lineId),
  });
  const addLine = () => onUpdate({ ...cat, line_items: [...cat.line_items, newLine()] });

  const total = cat.line_items.reduce((s, l) =>
    s + l.sub_items.reduce((ss, sub) => ss + (parseFloat(sub.estimated_total) || 0), 0)
  , 0);

  return (
    <div style={{
      border: '1px solid #000', borderRadius: 10,
      marginBottom: 14, overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,.04)',
    }}>
      {/* Category header bar */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a8a08 0%, #3b82f608 100%)',
        padding: '10px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
        borderBottom: '1.5px solid #e2e8f0',
      }}>
        <div style={{
          minWidth: 24, height: 24, borderRadius: 50,
          background: '#1e3a8a', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 800,
        }}>{index + 1}</div>
        <input
          style={{
            ...cellInp, flex: 1, fontWeight: 700, fontSize: 13,
            border: '1.5px solid #c7d2fe', background: '#f0f4ff',
          }}
          value={cat.category_name}
          onChange={setName}
          placeholder="Category name (e.g. Electrical, Mechanical, Civil…)"
        />
        {showRemove && (
          <button
            onClick={onRemove}
            title="Remove category"
            style={{
              padding: '6px 8px', borderRadius: 6, border: 'none',
              cursor: 'pointer', background: '#fee2e2', color: '#dc2626',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          ><TrashIcon /></button>
        )}
      </div>

      {/* Line items table */}
      <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
        <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr style={{ background: '#1e3a8a', borderBottom: '1.5px solid #1e3a8a' }}>
              {MAIN_HEADERS.map(h => (
                <th key={h} style={{
                  padding: '7px 6px', textAlign: 'center',
                  fontWeight: 700, fontSize: 10, textTransform: 'uppercase',
                  letterSpacing: '0.05em', color: '#fff', whiteSpace: 'nowrap',
                  background: '#1e3a8a',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cat.line_items.map(line => (
              <LineRow
                key={line._id}
                line={line}
                onChange={(updated) => updateLine(line._id, updated)}
                onRemove={() => removeLine(line._id)}
                showRemove={cat.line_items.length > 1}
                materials={materials}
                materialOptions={materialOptions}
                boqDocs={boqDocs}
                boqItemsCache={boqItemsCache}
                loadBoqItems={loadBoqItems}
                uomOptions={uomOptions}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div style={{
        padding: '8px 14px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16,
        background: '#fafbfc', borderTop: '1px solid #e2e8f0',
      }}>
        {total > 0 && (
          <span style={{ fontSize: 12, fontWeight: 700, color: '#1e3a8a' }}>
            Category Total: ₦{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        )}
        <button
          onClick={addLine}
          style={{
            padding: '5px 14px', borderRadius: 7, border: '1.5px dashed #93c5fd',
            background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer',
            fontWeight: 700, fontSize: 12,
          }}
        >+ Add Line Item</button>
      </div>
    </div>
  );
}

// ─── Main form ────────────────────────────────────────────────────────────────

export default function MRNewRequest({ onSaved, takeoffId = null }) {
  const { uomOptions } = useUOM();
  const [projects,      setProjects]      = useState([]);
  const [disciplines,   setDisciplines]   = useState([]);
  const [materials,     setMaterials]     = useState([]);
  const [me,            setMe]            = useState(null);
  const [boqDocs,       setBoqDocs]       = useState([]);
  const [boqItemsCache, setBoqItemsCache] = useState({});
  const [header, setHeader] = useState({
    project_id: '', building: '', discipline_id: '',
    revision: 'R0', priority: 'normal', notes: '', requested_date: '',
  });
  const [categories,   setCategories]   = useState([newCategory()]);
  const [saving,       setSaving]       = useState(false);
  const [successModal, setSuccessModal] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const toast = useToast();

  // Loaded from an approved take-off (Load Approved Take-Offs → Load): which one, so the request is
  // saved linked to it and the take-off records the quantities as requested.
  const [fromTakeoff, setFromTakeoff] = useState(null);   // { id, ref, revision, lines } | null
  const [takeoffError, setTakeoffError] = useState('');
  // Setting the project from a take-off must not trigger the "project changed → clear BOQ picks" reset.
  const keepBoqPicksRef = useRef(false);

  useEffect(() => {
    if (!takeoffId) return;
    let alive = true;
    api.getTakeoff(takeoffId).then(t => {
      if (!alive) return;
      if (t.status !== 'approved') { setTakeoffError(`${t.takeoff_ref} ${t.revision} is not approved, so a request cannot be raised from it.`); return; }
      const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
      const isOpen = (l) => (l.materials || []).length > 0
        && (num(l.boq_quantity) === null ? !num(l.total_transferred) : num(l.balance) > 0);
      let lineCount = 0;
      const cats = (t.systems || []).map(sys => {
        const lines = (sys.boq_lines || []).filter(isOpen).map(l => {
          lineCount += 1;
          return {
            ...newLine(),
            boq_id: sys.boq_id || '',
            boq_item_id: l.boq_item_id || '',
            required_quantity: num(l.boq_quantity) === null ? '' : String(num(l.balance)),
            unit_of_measure: l.boq_uom || '',
            remarks: `From ${t.takeoff_ref} ${t.revision} · BOQ ${l.boq_code || ''} ${l.boq_description}`.replace(/\s+/g, ' ').trim(),
            sub_items: l.materials.map(m => ({
              ...newSubItem(),
              material_id: m.material_id || 'others',
              material_description: m.description || '',
              specification: m.specification || '',
              brand_model: m.brand || '',
              required_quantity: String(m.total_quantity ?? m.required_quantity ?? ''),
              unit_of_measure: m.measured_uom || '',
            })),
          };
        });
        return lines.length ? { ...newCategory(), category_name: sys.system_name, line_items: lines } : null;
      }).filter(Boolean);
      if (!cats.length) { setTakeoffError(`Every BOQ line of ${t.takeoff_ref} has already been requested.`); return; }
      keepBoqPicksRef.current = true;
      setHeader(h => ({
        ...h,
        project_id: t.project_id,
        building: t.building_zone || '',
        discipline_id: t.discipline_id || '',
        notes: `Raised from material take-off ${t.takeoff_ref} ${t.revision}.`,
      }));
      setCategories(cats);
      setFromTakeoff({ id: t.id, ref: t.takeoff_ref, revision: t.revision, lines: lineCount });
      // the BOQ item pickers need their BOQ's items to show the pre-selected line
      [...new Set((t.systems || []).map(sys => sys.boq_id).filter(Boolean))].forEach(boqId => {
        api.getBOQ(boqId).then(r => setBoqItemsCache(c => ({ ...c, [boqId]: r?.boq_items || [] }))).catch(() => {});
      });
    }).catch(e => alive && setTakeoffError(e.message || 'Could not load the take-off.'));
    return () => { alive = false; };
  }, [takeoffId]);

  useEffect(() => {
    api.listProjects(0, 200).then(r => setProjects(r?.items || r || [])).catch(() => {});
    api.listDisciplines(0, 200).then(r => setDisciplines(r?.items || r || [])).catch(() => {});
    api.listMaterials(0, 500).then(r => {
      const list = Array.isArray(r) ? r : (r?.items || r?.data || []);
      setMaterials(list.filter(m => m.is_active !== false));
    }).catch(() => {});
    api.getMe().then(setMe).catch(() => {});
  }, []);

  useEffect(() => {
    setBoqDocs([]);
    // A project set from a take-off keeps the BOQ picks it came with; a project the user changes clears them.
    const keep = keepBoqPicksRef.current;
    keepBoqPicksRef.current = false;
    if (!keep) setBoqItemsCache({});
    if (!header.project_id) return;
    if (!keep) {
      setCategories(cs => cs.map(c => ({
        ...c,
        line_items: c.line_items.map(l => ({ ...l, boq_id: '', boq_item_id: '' })),
      })));
    }
    api.listProjectBOQs(header.project_id)
      .then(r => setBoqDocs(Array.isArray(r) ? r : (r?.items || [])))
      .catch(() => {});
  }, [header.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBoqItems = (boqDocId) => {
    if (!boqDocId || boqItemsCache[boqDocId]) return;
    api.getBOQ(boqDocId).then(r => {
      const items = r?.boq_items || [];
      setBoqItemsCache(c => ({ ...c, [boqDocId]: items }));
    }).catch(() => {});
  };

  const setHV = (k) => (v) => setHeader(h => ({ ...h, [k]: v }));
  const setH  = setHV;

  const updateCat   = (catId, updated) => setCategories(cs => cs.map(c => c._id === catId ? updated : c));
  const removeCat   = (catId) => setCategories(cs => cs.filter(c => c._id !== catId));
  const addCategory = () => setCategories(cs => [...cs, newCategory()]);

  const buildPayload = (submit) => {
    const cats = categories
      .filter(c => c.category_name.trim())
      .map(c => ({
        category_name: c.category_name.trim(),
        line_items: c.line_items.map(l => {
          const out = {};
          if (l.boq_item_id?.trim())        out.boq_item_id         = l.boq_item_id;
          if (l.required_quantity !== '')   out.required_quantity   = parseFloat(l.required_quantity);
          if (l.unit_of_measure?.trim())    out.unit_of_measure     = l.unit_of_measure.trim();
          if (l.estimated_unit_rate !== '') out.estimated_unit_rate = parseFloat(l.estimated_unit_rate);
          if (l.estimated_total !== '')     out.estimated_total     = parseFloat(l.estimated_total);
          if (l.remarks?.trim())            out.remarks             = l.remarks.trim();

          out.sub_items = l.sub_items
            .filter(s => s.material_id || s.material_description.trim())
            .map(s => {
              const sub = { material_description: s.material_description.trim() || '(to be specified)' };
              if (s.material_id && s.material_id !== 'others') sub.material_id         = s.material_id;
              if (s.specification?.trim())                     sub.specification       = s.specification.trim();
              if (s.brand_model?.trim())                       sub.brand_model         = s.brand_model.trim();
              if (s.required_quantity !== '')                  sub.required_quantity   = parseFloat(s.required_quantity);
              if (s.unit_of_measure?.trim())                   sub.unit_of_measure     = s.unit_of_measure.trim();
              if (s.estimated_unit_rate !== '')                sub.estimated_unit_rate = parseFloat(s.estimated_unit_rate);
              if (s.estimated_total !== '')                    sub.estimated_total     = parseFloat(s.estimated_total);
              return sub;
            });

          return out;
        }),
      }));

    return {
      project_id:     header.project_id,
      building:       header.building       || undefined,
      discipline_id:  header.discipline_id  || undefined,
      revision:       header.revision       || 'R0',
      priority:       header.priority,
      notes:          header.notes          || undefined,
      requested_date: header.requested_date || undefined,
      categories: cats,
      material_takeoff_id: fromTakeoff?.id || undefined,
      submit,
    };
  };

  const validate = () => {
    if (!header.project_id) return 'Please select a project.';
    const activeCats = categories.filter(c => c.category_name.trim());
    if (!activeCats.length) return 'Add at least one category with a name.';
    return null;
  };

  const handleSave = async (submit) => {
    const err = validate();
    if (err) { toast.show(err, 'error'); return; }
    setSaving(true);
    try {
      const project = projects.find(p => (p.id || p.project_id) === header.project_id);
      const code = project?.project_code || project?.code || 'PRJ';
      const res = await api.createMaterialRequest(buildPayload(submit), code);
      toast.show(submit ? 'Request submitted for review.' : 'Request saved as draft.', 'success');
      setSuccessModal({ submitted: submit, res });
    } catch (e) {
      toast.show(e?.response?.data?.detail || e?.detail || e?.message || 'Failed to save request.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const grandTotal = categories.reduce((s, cat) =>
    s + cat.line_items.reduce((cs, l) =>
      cs + l.sub_items.reduce((ss, sub) => ss + (parseFloat(sub.estimated_total) || 0), 0)
    , 0)
  , 0);

  const projectOptions = projects.map(p => ({ value: p.id || p.project_id, label: p.project_name || p.name }));
  const disciplineOptions = [
    { value: '', label: '— None —', alwaysVisible: true },
    ...disciplines.map(d => ({ value: d.id, label: d.name || d.discipline_name })),
  ];
  const materialOptions = [
    { value: 'others', label: 'Others', alwaysVisible: true },
    ...materials.map(m => ({ value: m.material_id, label: m.name })),
  ];

  const requesterName = me?.full_name || (me?.first_name && `${me.first_name} ${me.last_name || ''}`.trim()) || me?.username || '—';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {fromTakeoff && (
        <div style={{ padding: '12px 16px', borderRadius: 12, background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#065f46', fontSize: 13, lineHeight: 1.5 }}>
          <b>Loaded from take-off {fromTakeoff.ref} {fromTakeoff.revision}</b> — {fromTakeoff.lines} BOQ line{fromTakeoff.lines === 1 ? '' : 's'} still to request,
          with their materials. Review and adjust, then save or submit: the request stays linked to the take-off and these
          quantities are marked as requested there, so they cannot be requested twice.
        </div>
      )}
      {takeoffError && (
        <div style={{ padding: '12px 16px', borderRadius: 12, background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: 13 }}>
          {takeoffError}
        </div>
      )}

      {successModal && (
        <SuccessModal
          submitted={successModal.submitted}
          requestRef={successModal.res}
          onOk={() => { setSuccessModal(null); if (onSaved) onSaved(successModal.res); }}
        />
      )}

      {confirmSubmit && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, width: 440, maxWidth: '94vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.18)', overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              background: '#0b318bff', color: '#fff',
              padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
              <span style={{ fontWeight: 800, fontSize: 16 }}>Confirm Submission</span>
            </div>

            {/* Body */}
            <div style={{ padding: '24px 28px' }}>
              <p style={{ margin: '0 0 20px', fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
                You are about to submit this material request for <strong>Cost Control (CC) review</strong>.
                Once submitted it cannot be edited until reviewed.
              </p>

              {/* Summary */}
              <div style={{
                background: '#f1f5f9', borderRadius: 10, padding: '14px 18px',
                display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#64748b', fontWeight: 600 }}>Project</span>
                  <span style={{ fontWeight: 700, color: '#0f172a', textAlign: 'right', maxWidth: '60%' }}>
                    {projects.find(p => (p.id || p.project_id) === header.project_id)?.project_name || '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#64748b', fontWeight: 600 }}>Categories</span>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>
                    {categories.filter(c => c.category_name.trim()).length}
                  </span>
                </div>
                {grandTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderTop: '1px solid #e2e8f0', paddingTop: 8, marginTop: 4 }}>
                    <span style={{ color: '#64748b', fontWeight: 600 }}>Estimated Total</span>
                    <span style={{ fontWeight: 800, color: '#0b318bff' }}>
                      ₦{grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  disabled={saving}
                  onClick={() => setConfirmSubmit(false)}
                  style={{
                    padding: '10px 20px', borderRadius: 8, border: '1.5px solid #cbd5e1',
                    background: '#fff', color: '#475569', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                    opacity: saving ? 0.5 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  disabled={saving}
                  onClick={() => { setConfirmSubmit(false); handleSave(true); }}
                  style={{
                    padding: '10px 24px', borderRadius: 8, border: 'none',
                    background: saving ? '#86efac' : '#16a34a', color: '#fff',
                    cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  {saving ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.7s linear infinite' }}>
                        <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.35)" strokeWidth="2.6"/>
                        <path d="M12 3a9 9 0 0 1 9 9" stroke="#fff" strokeWidth="2.6" strokeLinecap="round"/>
                      </svg>
                      Submitting…
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                      Yes, Submit for CC Review
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Page title ── */}
      <div>
        <h2 style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 20, color: 'var(--text-primary)' }}>
          New Material Request
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
          Fill in the header, add categories and line items, then save as draft or submit for CC review.
        </p>
      </div>

      {/* ── Section 1 — Request Header ── */}
      <Card style={{ padding: '18px 22px' }}>
        <div style={{
          fontWeight: 800, fontSize: 11, color: '#1e3a8a',
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 18,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <div style={{ width: 3, height: 14, borderRadius: 2, background: '#1e3a8a' }} />
          Section 1 — Request Details
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Requester
            </div>
            <div style={{
              padding: '9px 12px', borderRadius: 7, border: '1.5px solid var(--border)',
              background: '#f1f5f9', color: '#1e3a8a', fontSize: 13, fontWeight: 600,
            }}>
              {requesterName}
            </div>
          </div>

          <Select label="Project *" title="Select Project" value={header.project_id} onChange={setHV('project_id')} options={projectOptions} required />
          <DatePicker label="Request Date" value={header.requested_date} onChange={setHV('requested_date')} />
          <Input label="Building / Block" value={header.building} onChange={setH('building')} placeholder="e.g. Block A — Residential Tower" />
          <Select label="Discipline" title="Select Discipline" value={header.discipline_id} onChange={setHV('discipline_id')} options={disciplineOptions} />
          <Input label="Revision" value={header.revision} onChange={setH('revision')} placeholder="R0" />
          <Select label="Priority" title="Select Priority" value={header.priority} onChange={setHV('priority')} options={PRIORITY_OPTIONS} />
          <div style={{ gridColumn: 'span 2' }}>
            <Textarea label="Notes" value={header.notes} onChange={setH('notes')} placeholder="Optional notes or remarks about this request…" rows={3} />
          </div>
        </div>
      </Card>

      {/* ── Section 2 — Categories & Line Items ── */}
      <Card style={{ padding: '18px 22px' }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontWeight: 800, fontSize: 11, color: '#1e3a8a',
            textTransform: 'uppercase', letterSpacing: '0.08em',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div style={{ width: 3, height: 14, borderRadius: 2, background: '#1e3a8a' }} />
            Section 2 — Categories &amp; Line Items
          </div>
        </div>

        {categories.map((cat, i) => (
          <CategoryBlock
            key={cat._id}
            cat={cat}
            index={i}
            onUpdate={(updated) => updateCat(cat._id, updated)}
            onRemove={() => removeCat(cat._id)}
            showRemove={categories.length > 1}
            materials={materials}
            materialOptions={materialOptions}
            boqDocs={boqDocs}
            boqItemsCache={boqItemsCache}
            loadBoqItems={loadBoqItems}
            uomOptions={uomOptions}
          />
        ))}

        <button
          onClick={addCategory}
          style={{
            width: '100%', padding: '10px', borderRadius: 8,
            border: 'none', background: '#1e3a8a',
            color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13,
            marginTop: 4,
          }}
        >Add Another Category</button>

        {grandTotal > 0 && (
          <div style={{
            textAlign: 'right', marginTop: 14, paddingTop: 12,
            borderTop: '1.5px solid #e2e8f0',
            fontSize: 15, fontWeight: 800, color: '#1e3a8a',
          }}>
            Grand Total: ₦{grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        )}
      </Card>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end', paddingBottom: 8 }}>
        <button
          disabled={saving}
          onClick={onSaved}
          style={{
            padding: '10px 20px', borderRadius: 9, border: 'none',
            cursor: 'pointer', fontWeight: 700, fontSize: 13,
            background: '#dc2626', color: '#fff',
            display: 'flex', alignItems: 'center', gap: 6,
            opacity: saving ? 0.6 : 1,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Cancel
        </button>
        <button
          disabled={saving}
          onClick={() => handleSave(false)}
          style={{
            padding: '10px 20px', borderRadius: 9, border: 'none',
            cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13,
            background: '#d97706', color: '#fff',
            display: 'flex', alignItems: 'center', gap: 6,
            opacity: saving ? 0.6 : 1,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
          {saving ? 'Saving…' : 'Save to Draft'}
        </button>
        <button
          disabled={saving}
          onClick={() => {
            const err = validate();
            if (err) { toast.show(err, 'error'); return; }
            setConfirmSubmit(true);
          }}
          style={{
            padding: '10px 22px', borderRadius: 9, border: 'none',
            cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13,
            background: '#16a34a', color: '#fff',
            display: 'flex', alignItems: 'center', gap: 6,
            opacity: saving ? 0.6 : 1,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          Submit for CC Review
        </button>
      </div>
    </div>
  );
}
